import { createToastHook } from "@gluestack-ui/core/toast/creator";
import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { AnimatePresence, Motion, MotionComponentProps } from "@legendapp/motion";
import { cssInterop } from "nativewind";
import React from "react";
import { AccessibilityInfo, Text, View, ViewStyle } from "react-native";
import { IIconProps, Icon } from "../Icon/Icon";

type IMotionViewProps = React.ComponentProps<typeof View> &
	MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>;

const MotionView = Motion.View as React.ComponentType<IMotionViewProps>;

const useToast = createToastHook(MotionView, AnimatePresence);
const SCOPE = "TOAST";

cssInterop(MotionView, { className: "style" });

const toastStyle = tva({
	base: "p-2 m-1 rounded-xl web:pointer-events-auto border",
	variants: {
		action: {
			error: "bg-error-700",
			warning: "bg-warning-700",
			success: "bg-success-800",
			info: "bg-tertiary-500",
			muted: "bg-tertiary-500",
		},
		variant: {
			solid: "",
			outline: "bg-neutral-0",
		},
	},
	compoundVariants: [
		{
			variant: "solid",
			action: ["muted", "info"],
			className: "border-tertiary-700",
		},
		{
			variant: "solid",
			action: "error",
			className: "border-error-900",
		},
		{
			variant: "solid",
			action: "success",
			className: "border-success-800",
		},
		{
			variant: "solid",
			action: "warning",
			className: "border-warning-700",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "border-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "border-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "border-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "border-warning-700",
		},
	],
});

const toastTitleStyle = tva({
	base: "text-neutral-950 font-sans font-semibold text-base text-left",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "",
			warning: "",
			success: "",
			info: "",
			muted: "",
		},
	},
	parentCompoundVariants: [
		{
			variant: "solid",
			action: ["error", "success", "warning"],
			className: "text-neutral-0",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "text-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "text-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "text-warning-700",
		},
	],
});

const toastDescriptionStyle = tva({
	base: "text-neutral-950 font-sans font-normal text-sm text-left break-words",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "",
			warning: "",
			success: "",
			info: "",
			muted: "",
		},
	},
	parentCompoundVariants: [
		{
			variant: "solid",
			action: ["error", "success", "warning"],
			className: "text-neutral-0",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "text-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "text-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "text-warning-700",
		},
	],
});

const Root = withStyleContext(View, SCOPE);

export type IToastProps = React.ComponentProps<typeof Root> & {
	className?: string;
} & VariantProps<typeof toastStyle>;

const Toast = React.forwardRef<React.ComponentRef<typeof Root>, IToastProps>(function Toast(
	{ className, variant = "solid", action = "muted", ...props },
	ref,
) {
	return (
		<Root
			ref={ref}
			className={toastStyle({ variant, action, class: className })}
			context={{ variant, action }}
			{...props}
		/>
	);
});

type IToastTitleProps = React.ComponentProps<typeof Text> & {
	className?: string;
} & VariantProps<typeof toastTitleStyle>;

const ToastTitle = React.forwardRef<React.ComponentRef<typeof Text>, IToastTitleProps>(function ToastTitle(
	{ className, children, ...props },
	ref,
) {
	const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);
	React.useEffect(() => {
		// Issue from react-native side
		// Hack for now, will fix this later
		AccessibilityInfo.announceForAccessibility(children as string);
	}, [children]);

	return (
		<Text
			{...props}
			ref={ref}
			aria-live="assertive"
			aria-atomic="true"
			role="alert"
			className={toastTitleStyle({
				class: className,
				parentVariants: {
					variant: parentVariant,
					action: parentAction,
				},
			})}
		>
			{children}
		</Text>
	);
});

type IToastDescriptionProps = React.ComponentProps<typeof Text> & {
	className?: string;
} & VariantProps<typeof toastDescriptionStyle>;

const ToastDescription = React.forwardRef<React.ComponentRef<typeof Text>, IToastDescriptionProps>(
	function ToastDescription({ className, ...props }, ref) {
		const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);
		return (
			<Text
				ref={ref}
				{...props}
				className={toastDescriptionStyle({
					class: className,
					parentVariants: {
						variant: parentVariant,
						action: parentAction,
					},
				})}
			/>
		);
	},
);

const toastIconStyle = tva({
	base: "fill-none text-neutral-950",
	parentVariants: {
		variant: {
			solid: "",
			outline: "",
		},
		action: {
			error: "",
			warning: "",
			success: "",
			info: "",
			muted: "",
		},
	},
	parentCompoundVariants: [
		{
			variant: "solid",
			action: ["error", "success", "warning"],
			className: "text-neutral-0",
		},
		{
			variant: "outline",
			action: ["muted", "info"],
			className: "text-tertiary-950",
		},
		{
			variant: "outline",
			action: "error",
			className: "text-error-700",
		},
		{
			variant: "outline",
			action: "success",
			className: "text-success-900",
		},
		{
			variant: "outline",
			action: "warning",
			className: "text-warning-700",
		},
	],
});

const ToastIcon = ({ className, ...props }: IIconProps) => {
	const { variant: parentVariant, action: parentAction } = useStyleContext(SCOPE);

	return (
		<Icon
			{...props}
			size="sm"
			className={toastIconStyle({
				parentVariants: {
					variant: parentVariant,
					action: parentAction,
				},
				class: className,
			})}
		/>
	);
};

Toast.displayName = "Toast";
ToastTitle.displayName = "ToastTitle";
ToastDescription.displayName = "ToastDescription";
ToastIcon.displayName = "ToastIcon";

export { useToast, Toast, ToastTitle, ToastDescription, ToastIcon };
